import formatMoney from "lib/formatMoney";
import dayjs from "dayjs";

const CalculatorQuestions = [
  {
    q_id: "0",
    q_title: "Purchase price",
    q_description: () => {
      return "What is the purchase price of the land?";
    },
    node_type: "node",
    a_type: "currency",
    a_input_type: "number",
    a_opts: () => [],
    q_next_step_func: (answer) => answer.q_id + "0",
    q_child_nodes: [0],
    q_validation_func: (answerVal) => {
      return answerVal > 0;
    },
    q_invalid_answer_help_text: "Please enter a value above 0",
    a_error: false,
  },
  {
    q_id: "00",
    q_title: "Loan to value",
    q_description: () => {
      return "What loan-to-value (LTV) percentage do you want? (Example: 75% means you'll put 25% down)";
    },
    node_type: "node",
    a_type: "percent",
    a_input_type: "number",
    a_opts: () => [],
    q_next_step_func: (answer) => answer.q_id + "0",
    q_child_nodes: [0],
    q_validation_func: (answerVal) => {
      return answerVal > 0 && answerVal <= 101; // Most land loans max out at 80% LTV
    },
    q_invalid_answer_help_text: "Please enter an LTV between 1 and 100",
    a_error: false,
  },
  {
    q_id: "000",
    q_title: "Amortization type",
    q_description: () => {
      return "Would you like principal and interest payments, or interest only payments?";
    },
    node_type: "node",
    a_type: "mc",
    a_input_type: "",
    a_opts: () => ["Principal and interest", "Interest only"],
    q_next_step_func: (answer) => answer.q_id + "0",
    q_child_nodes: [0],
    q_validation_func: () => true,
    q_invalid_answer_help_text: "",
    a_error: false,
    a_string: "Principal and interest",
  },
  {
    q_id: "0000",
    q_title: "Term",
    q_description: () => {
      return "How many years will the loan term be?";
    },
    node_type: "node",
    a_type: "number",
    a_input_type: "number",
    q_next_step_func: (answer) => answer.q_id + "0",
    q_child_nodes: [0],
    q_validation_func: () => true,
    q_invalid_answer_help_text: "",
    a_error: false,
    q_placeholder: "Years",
  },
  {
    q_id: "00000",
    q_title: "Interest rate",
    q_description: () => {
      return "What is the annual interest rate for the loan?";
    },
    node_type: "node",
    a_type: "percent",
    a_input_type: "number",
    a_opts: () => [],
    q_next_step_func: (answer) => answer.q_id + "0",
    q_child_nodes: [0],
    q_validation_func: (answerVal) => {
      return answerVal > 0 && answerVal <= 30;
    },
    q_invalid_answer_help_text: "Please enter a rate between 0.1 and 30",
    a_error: false,
  },
  {
    q_id: "000000",
    q_title: "Property tax",
    q_description: () => {
      return "What is the estimated annual property tax?";
    },
    node_type: "node",
    a_type: "currency",
    a_input_type: "number",
    a_opts: () => [],
    q_next_step_func: (answer) => answer.q_id + "0",
    q_child_nodes: [0],
    q_validation_func: (answerVal) => answerVal >= 0,
    q_invalid_answer_help_text: "Please enter a value of 0 or greater",
    a_error: false,
    q_placeholder: "Annual amount",
  },
  {
    q_id: "0000000",
    q_title: "Insurance",
    q_description: () => {
      return "What is the estimated annual insurance cost, if any?";
    },
    node_type: "node",
    a_type: "currency",
    a_input_type: "number",
    a_opts: () => [],
    q_next_step_func: (answer) => answer.q_id + "0",
    q_child_nodes: [0],
    q_validation_func: (answerVal) => answerVal >= 0,
    q_invalid_answer_help_text: "Please enter a value of 0 or greater",
    a_error: false,
    q_placeholder: "Annual amount",
  },
  {
    q_id: "00000000",
    q_title: "Payment frequency",
    q_description: () => {
      return "How often would you like to make payments?";
    },
    node_type: "node",
    a_type: "mc",
    a_input_type: "",
    a_opts: () => ["Monthly", "Quarterly", "Semi-annual", "Annual"],
    q_next_step_func: (answer) => answer.q_id + "0",
    q_child_nodes: [0],
    q_validation_func: () => true,
    q_invalid_answer_help_text: "",
    a_error: false,
    a_string: "Monthly", // Set default value
  },
  {
    q_id: "000000000",
    q_title: "Origination fee",
    q_description: () => {
      return "What is the loan origination fee? This is typically charged by the lender as a percentage of the loan amount.";
    },
    node_type: "node",
    a_type: "currency",
    a_input_type: "number",
    a_opts: () => [],
    q_next_step_func: (answer) => answer.q_id + "0",
    q_child_nodes: [0],
    q_validation_func: (answerVal) => answerVal >= 0,
    q_invalid_answer_help_text: "Please enter a value of 0 or greater",
    a_error: false,
  },
  {
    q_id: "0000000000",
    q_title: "Loan fees",
    q_description: () => {
      return "What are the additional loan fees? This includes processing fees, underwriting fees, etc.";
    },
    node_type: "node",
    a_type: "currency",
    a_input_type: "number",
    a_opts: () => [],
    q_next_step_func: (answer) => answer.q_id + "0",
    q_child_nodes: [0],
    q_validation_func: (answerVal) => answerVal >= 0,
    q_invalid_answer_help_text: "Please enter a value of 0 or greater",
    a_error: false,
    a_string: "1995", // Default value
  },
  {
    q_id: "00000000000",
    q_title: "Title fees",
    q_description: () => {
      return "What are the estimated title fees? This includes title search, title insurance, and related fees.";
    },
    node_type: "node",
    a_type: "currency",
    a_input_type: "number",
    a_opts: () => [],
    q_next_step_func: (answer) => answer.q_id + "0",
    q_child_nodes: [0],
    q_validation_func: (answerVal) => answerVal >= 0,
    q_invalid_answer_help_text: "Please enter a value of 0 or greater",
    a_error: false,
  },
  {
    q_id: "000000000000",
    q_title: "Escrow",
    q_description: () => {
      return "What are the estimated escrow fees? This includes escrow company fees and initial escrow deposits.";
    },
    node_type: "node",
    a_type: "currency",
    a_input_type: "number",
    a_opts: () => [],
    q_next_step_func: (answer) => answer.q_id + "0",
    q_child_nodes: [0],
    q_validation_func: (answerVal) => answerVal >= 0,
    q_invalid_answer_help_text: "Please enter a value of 0 or greater",
    a_error: false,
  },
  {
    q_id: "0000000000000",
    q_title: "Prepaid interest",
    q_description: () => {
      return "What is the estimated prepaid interest? This is interest paid from closing to the first payment date.";
    },
    node_type: "node",
    a_type: "currency",
    a_input_type: "number",
    a_opts: () => [],
    q_next_step_func: (answer) => answer.q_id + "0",
    q_child_nodes: [0],
    q_validation_func: (answerVal) => answerVal >= 0,
    q_invalid_answer_help_text: "Please enter a value of 0 or greater",
    a_error: false,
  },
  {
    q_id: "00000000000000",
    q_title: "Prepaid insurance",
    q_description: () => {
      return "What is the estimated prepaid insurance? This typically includes the first year's insurance premium.";
    },
    node_type: "node",
    a_type: "currency",
    a_input_type: "number",
    a_opts: () => [],
    q_next_step_func: (answer) => answer.q_id + "0",
    q_child_nodes: [0],
    q_validation_func: (answerVal) => answerVal >= 0,
    q_invalid_answer_help_text: "Please enter a value of 0 or greater",
    a_error: false,
  },
  {
    q_id: "000000000000000",
    q_title: "Calculation name",
    q_description: () => {
      return "What would you like to name this calculation? Ex. 'Land loan calc for 123 Acre Lot'";
    },
    node_type: "node",
    a_type: "text",
    a_input_type: "text",
    a_opts: () => [],
    q_next_step_func: (answer) => answer.q_id + "0",
    q_child_nodes: [0],
    q_validation_func: () => true,
    q_invalid_answer_help_text: "",
    a_error: false,
  },
  {
    q_id: "0000000000000000",
    q_title: "Address (optional)",
    q_description: () => {
      return "Enter property address to help organize your calculations. You can also enter a state, zip or nothing, since this field is optional.";
    },
    node_type: "node",
    a_type: "address",
    a_opts: () => [],
    q_next_step_func: (answer) => answer.q_id + "0",
    q_child_nodes: [0],
    q_validation_func: (answerVal) => {
      if (!answerVal || answerVal.length === 0) return true;
      return answerVal.length > 0;
    },
    q_invalid_answer_help_text: "Please enter or select an address",
    a_error: false,
  },
];

export default CalculatorQuestions;
